import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faSpinner, 
  faExclamationTriangle,
  faCheckCircle,
  faEdit,
  faArchive,
  faCircleCheck,
  faCircleXmark,
  faCircleExclamation,
  faEnvelope,
  faShield,
  faCalendar,
  faUserPlus,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../contexts/AuthContext';
import ArchiveUserModal from '../../../components/ArchiveUserModal';
import api from '../../../Api';

const ChefTerrainDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [chefTerrain, setChefTerrain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    status: 'active'
  });

  useEffect(() => {
    fetchChefTerrain();
  }, [id]);

  const fetchChefTerrain = async () => {
    try {
      setLoading(true);
      console.log('Fetching chef terrain with ID:', id);
      console.log('ID type:', typeof id);
      console.log('API URL:', `/users/${id}`);
      
      const response = await api.get(`/users/${id}`);
      console.log('Chef terrain response:', response.data);
      setChefTerrain(response.data);
      setFormData({
        name: response.data.name || '',
        email: response.data.email || '',
        status: response.data.status || 'active'
      });
    } catch (error) {
      console.error('Error fetching chef terrain:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      setError(`Erreur lors du chargement du chef de terrain: ${error.response?.data?.message || error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setErrors({});

      // Only send name and status, exclude role
      const updateData = {
        name: formData.name,
        status: formData.status
      };
      
      console.log('Sending update data:', updateData);
      await api.put(`/users/${id}`, updateData);
      setSuccess(true);
      setEditing(false);
      fetchChefTerrain(); // Refresh chef terrain data
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating chef terrain:', error);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: 'Erreur lors de la mise à jour du chef de terrain' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: chefTerrain?.name || '',
      email: chefTerrain?.email || '',
      status: chefTerrain?.status || 'active'
    });
    setEditing(false);
    setErrors({});
  };

  const handleArchive = () => {
    setArchiveModalOpen(true);
  };

  const handleArchiveSuccess = () => {
    navigate('/chef-terrains');
  };

  const canModifyUser = () => {
    return currentUser?.role === 'chef_terrain_superieur';
  };

  const canArchiveUser = () => {
    return currentUser?.role === 'chef_terrain_superieur';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <FontAwesomeIcon icon={faCircleCheck} className="text-green-500" />;
      case 'suspended':
        return <FontAwesomeIcon icon={faCircleXmark} className="text-red-500" />;
      case 'inactive':
        return <FontAwesomeIcon icon={faCircleExclamation} className="text-yellow-500" />;
      default:
        return <FontAwesomeIcon icon={faCircleXmark} className="text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'suspended':
        return 'Suspendu';
      case 'inactive':
        return 'Inactif (Automatique)';
      default:
        return 'Inconnu';
    }
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'chef_terrain': 'Chef de Terrain',
      'chef_terrain_superieur': 'Head of operations'
    };
    return roleMap[role] || role;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du chef de terrain...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="relative z-10 flex items-center justify-center min-h-screen p-6">
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-8 text-center max-w-md w-full">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-6xl text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/chef-terrains')}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-semibold"
            >
              Retour aux Chefs de Terrain
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 flex justify-between items-center p-6 animate-fade-in">
        <div className="flex items-center space-x-8">
          <h1 className="text-3xl font-bold text-gray-800">
            DÉTAILS DU CHEF DE TERRAIN
          </h1>
        </div>
        <div className="flex items-center space-x-4">
          {canModifyUser() && (
            <button
              onClick={() => setEditing(!editing)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FontAwesomeIcon icon={faEdit} className="mr-2" />
              {editing ? 'Annuler' : 'Modifier'}
            </button>
          )}
          {canArchiveUser() && (
            <button
              onClick={handleArchive}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <FontAwesomeIcon icon={faArchive} className="mr-2" />
              Archiver
            </button>
          )}
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-6 animate-slide-up">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-8">
            
            {/* Error Message */}
            {(errors.general || error) && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{errors.general || error}</p>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 text-sm">Chef de terrain mis à jour avec succès</p>
              </div>
            )}

            {/* Chef Terrain Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Basic Information */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Informations de base
                </h3>
                
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faUser} className="mr-2" />
                    Nom complet
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{chefTerrain?.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                    Adresse e-mail
                  </label>
                  <p className="text-gray-900 font-medium">{chefTerrain?.email}</p>
                  {editing && (
                    <p className="text-xs text-gray-500 mt-1">L'adresse email ne peut pas être modifiée</p>
                  )}
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faShield} className="mr-2" />
                    Rôle
                  </label>
                  <p className="text-gray-900 font-medium">{getRoleDisplayName(chefTerrain?.role)}</p>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  {editing ? (
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    >
                      <option value="active">Actif</option>
                      <option value="suspended">Suspendu</option>
                    </select>
                  ) : (
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                      chefTerrain?.status === 'active' ? 'text-green-600 bg-green-100' :
                      chefTerrain?.status === 'suspended' ? 'text-red-600 bg-red-100' :
                      'text-gray-600 bg-gray-100'
                    }`}>
                      {getStatusText(chefTerrain?.status)}
                    </span>
                  )}
                </div>
              </div>

              {/* Metadata Information */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Métadonnées
                </h3>
                
                {/* Created By */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                    Créé par
                  </label>
                  <p className="text-gray-900 font-medium">{chefTerrain?.created_by || 'Système'}</p>
                </div>

                {/* Created At */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                    Date de création
                  </label>
                  <p className="text-gray-900 font-medium">{formatDate(chefTerrain?.created_at)}</p>
                </div>

                {/* Last Login */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                    Dernière connexion
                  </label>
                  <p className="text-gray-900 font-medium">{formatDate(chefTerrain?.last_login)}</p>
                </div>

                {/* Chef Terrain ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ID Chef de Terrain
                  </label>
                  <p className="text-gray-500 font-mono text-sm">{chefTerrain?.id}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {editing && (
              <div className="flex justify-end space-x-4 pt-8 border-t border-gray-200 mt-8">
                <button
                  onClick={handleCancel}
                  className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
                >
                  {saving ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      Sauvegarder
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Archive Modal */}
      {archiveModalOpen && (
        <ArchiveUserModal
          isOpen={archiveModalOpen}
          onClose={() => setArchiveModalOpen(false)}
          onArchive={handleArchiveSuccess}
          user={chefTerrain}
        />
      )}
    </div>
  );
};

export default ChefTerrainDetail;
